/**
 * @file ContractDrawerAddDocumentActionView.tsx
 * @description "+ Add Document" CTA shown in the SAVED-extraction
 *   drawer body (SE-4347 AC-4). Tapping it opens the existing
 *   `DiscardExtractionDialog` so the user must explicitly confirm
 *   delete-all-data before re-uploading.
 * @module features/contracts/components/ContractDrawerAddDocumentAction/ContractDrawerAddDocumentActionView
 */

import { Plus } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";

/** Props for {@link ContractDrawerAddDocumentActionView}. */
interface Props {
  /** Invoked on press — opens the DiscardExtractionDialog upstream. */
  readonly onPress: () => void;
}

/**
 * Renders the "+ Add Document" CTA used to start the re-upload flow
 * from a SAVED extraction.
 * @param props - View props
 * @param props.onPress - Tap handler (opens the DiscardExtractionDialog upstream)
 * @returns The rendered button
 * @remarks Wrapping in a `View` keeps the testID reachable on web
 *   (RNW translates `testID` on `View` to `data-testid`) without
 *   tripping GlueStack's HStack-on-web type that lacks `testID`.
 */
const ContractDrawerAddDocumentActionView = ({ onPress }: Props) => (
  <Box testID="contract-drawer-add-document-action">
    <Button
      testID="contract-drawer-add-document-btn"
      label="Add Document"
      size="sm"
      variant="outline"
      tone="primary"
      icon={Plus}
      onPress={onPress}
      className="self-start"
    />
  </Box>
);

ContractDrawerAddDocumentActionView.displayName =
  "ContractDrawerAddDocumentActionView";

export default memo(ContractDrawerAddDocumentActionView);
